// A durable, first-party sink for browser exceptions, independent of Sentry.
//
// Sentry remains the primary error channel and is untouched by this module.
// This is a SECOND, unrelated transport that POSTs a normalized, aggregated
// summary of the same failures to our own backend, so that a Sentry outage
// leaves something behind to reconcile against. Between 2026-08-10T05:39:27Z
// and 2026-08-16T10:40:39Z Sentry returned zero production error events, and
// the browser surface had no other record at all — which is not evidence that
// no browser errors happened, only that nothing could be looked at.
//
// Three properties make this a sink rather than a second copy of Sentry:
//
//  1. It never goes through the Sentry SDK. Capture hangs off plain `window`
//     listeners and the root ErrorBoundary, deliberately NOT off `beforeSend`.
//     With no DSN configured, `initSentry()` returns before `Sentry.init` and
//     there is no client at all, so no SDK callback of any kind runs; and even
//     with a client, `sampleRate` and `ignoreErrors` discard events upstream of
//     `beforeSend` (@sentry/core `client.js` gates on sampleRate before
//     `_prepareEvent`, whose event processors apply `ignoreErrors`, and both
//     precede `processBeforeSend`). An SDK hook can only ever see the subset of
//     failures the SDK itself decided to keep.
//  2. It sends shapes, not payloads. Every volatile or sensitive substring in a
//     message is replaced with a placeholder before the result is hashed into a
//     short signature. No stacks, no raw messages, no request data, no cookies,
//     and no URL query strings are persisted.
//  3. It is bounded. Repeated errors increment a counter instead of issuing a
//     request each; distinct signatures per flush are capped; a full buffer
//     drops instead of growing.

import { buildApiUrl } from './api';
import { SENSITIVE_QUERY_PARAMS, scrubUrl } from './sentry';

/** Wire contract agreed with the backend's `POST /api/client-errors`. */
export const CLIENT_ERROR_SCHEMA = 'browser_error_v1';
export const CLIENT_ERROR_PATH = '/api/client-errors';

/** Distinct signatures held per flush window. Also the payload's max length. */
export const MAX_TRACKED_SIGNATURES = 20;

/** Saturation point for a single signature's counter within one flush window. */
export const MAX_OCCURRENCES_PER_SIGNATURE = 1_000;

/** Delay between the first buffered error and its flush. */
export const FLUSH_INTERVAL_MS = 5_000;

const MAX_RAW_MESSAGE_LENGTH = 2_000;
const MAX_MESSAGE_SHAPE_LENGTH = 200;
const MAX_ERROR_NAME_LENGTH = 64;
const MAX_ROUTE_SEGMENTS = 8;
const MAX_ROUTE_SEGMENT_LENGTH = 32;

/** Sent as `release` when the build was not stamped (any local build). */
const UNKNOWN_RELEASE = 'unknown';

/** Sent as `routeClass` when the location cannot be read at all. */
const UNKNOWN_ROUTE_CLASS = '<unknown>';

export interface ClientErrorEntry {
  signature: string;
  name: string;
  messageShape: string;
  occurrences: number;
  firstSeen: string;
  lastSeen: string;
}

export interface ClientErrorPayload {
  schema: typeof CLIENT_ERROR_SCHEMA;
  release: string;
  routeClass: string;
  errors: ClientErrorEntry[];
  sinkFailures: ClientErrorSinkFailure[];
}

export type ClientErrorSinkFailureReason =
  | 'signature_cap'
  | 'guard_error'
  | 'route_read_error'
  | 'payload_error'
  | 'send_beacon_error'
  | 'fetch_unavailable'
  | 'fetch_error'
  | 'fetch_rejected'
  | 'fetch_http_error';

export interface ClientErrorSinkFailure {
  reason: ClientErrorSinkFailureReason;
  droppedEntries: number;
}

/** Fixed reason set plus saturating counts keeps self-observation bounded. */
const MAX_ACCOUNTED_SINK_FAILURES = 100_000;
const sinkFailureCounts = new Map<ClientErrorSinkFailureReason, number>();

const recordSinkFailure = (
  reason: ClientErrorSinkFailureReason,
  droppedEntries: number,
): void => {
  const increment = Math.max(0, Math.floor(droppedEntries));
  const current = sinkFailureCounts.get(reason) ?? 0;
  sinkFailureCounts.set(reason, Math.min(MAX_ACCOUNTED_SINK_FAILURES, current + increment));
};

const snapshotSinkFailures = (): ClientErrorSinkFailure[] =>
  Array.from(sinkFailureCounts, ([reason, droppedEntries]) => ({ reason, droppedEntries }));

const acknowledgeSinkFailures = (reported: readonly ClientErrorSinkFailure[]): void => {
  for (const failure of reported) {
    const current = sinkFailureCounts.get(failure.reason) ?? 0;
    const remaining = current - failure.droppedEntries;
    if (remaining > 0) sinkFailureCounts.set(failure.reason, remaining);
    else sinkFailureCounts.delete(failure.reason);
  }
};

/**
 * Runs `body` without letting a reporter failure escape into the page.
 *
 * Failures are counted in a fixed, saturating reason map and ride the next
 * successful beacon as `sinkFailures`. That keeps the page fail-open without
 * making sink loss silent or creating a recursive retry path.
 */
const safely = (body: () => void): void => {
  try {
    body();
  } catch {
    recordSinkFailure('guard_error', 1);
  }
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// ──────────────────────────────────────────────────────────────────────
// Route class
// ──────────────────────────────────────────────────────────────────────

const UUID_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT_RE = /^\d+$/;
const HEX_SEGMENT_RE = /^[0-9a-f]{16,}$/i;
const SLUG_SEGMENT_RE = /^[A-Za-z0-9._-]+$/;

const normalizeRouteSegment = (segment: string): string => {
  if (segment.length > MAX_ROUTE_SEGMENT_LENGTH) return ':id';
  if (UUID_SEGMENT_RE.test(segment)) return ':id';
  if (NUMERIC_SEGMENT_RE.test(segment)) return ':id';
  if (HEX_SEGMENT_RE.test(segment)) return ':id';
  // Long mixed alphanumerics are opaque identifiers (Shopify gids, Stripe ids,
  // nanoids), not route names.
  if (segment.length >= 12 && /\d/.test(segment) && /[A-Za-z]/.test(segment)) return ':id';
  // Anything that is not a plain slug is data rather than a route name: a
  // pathname can carry an email address, a percent-encoded search term, or a
  // merchant-supplied brand name.
  if (!SLUG_SEGMENT_RE.test(segment)) return ':id';
  return segment;
};

/**
 * Collapse a pathname to the route pattern it belongs to: `/brands/:id/reports`
 * rather than `/brands/6f0c…/reports`. Exported because it is the one piece of
 * this module that must be readable next to a real URL to be checked.
 */
export const routeClassFromPathname = (pathname: string): string => {
  const segments = pathname.split('/').filter((segment) => segment.length > 0);
  const kept = segments.slice(0, MAX_ROUTE_SEGMENTS).map(normalizeRouteSegment);
  if (segments.length > MAX_ROUTE_SEGMENTS) kept.push('...');
  return `/${kept.join('/')}`;
};

// The beacon installs at module scope, outside every `<BrowserRouter>` in the
// app, so react-router's matched path is not reachable without rendering a hook
// component inside each of the three routers (App's two, plus main.tsx's public
// shell). It would return the same string anyway: every route in App.tsx is a
// literal with no path params, so a normalized pathname IS the matched pattern.
// Revisit only if a parameterized route is added whose id shape this misses.
const currentRouteClass = (): string => {
  try {
    return routeClassFromPathname(window.location.pathname);
  } catch {
    recordSinkFailure('route_read_error', 0);
    return UNKNOWN_ROUTE_CLASS;
  }
};

// ──────────────────────────────────────────────────────────────────────
// Normalization
//
// Two ordered rule sets. REDACTION_RULES remove things that must never be
// persisted; SHAPE_RULES additionally remove things that are merely volatile,
// so that the same logical failure hashes to one signature no matter which
// values it happened to carry. Order within each list matters: URLs are
// rewritten before the token and digit rules can shred them into placeholders,
// and quoted contents are collapsed last so the redactors see the real text.
// ──────────────────────────────────────────────────────────────────────

type ShapeReplacer = (match: string, ...groups: string[]) => string;

interface TextRule {
  readonly pattern: RegExp;
  readonly replace: ShapeReplacer;
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const TRAILING_PUNCTUATION_RE = /[.,;:!?]+$/;

const normalizeUrlForShape = (raw: string): string => {
  const url = raw.replace(TRAILING_PUNCTUATION_RE, '');
  // sentry.ts's helper, reused rather than reimplemented so the two sinks can
  // never disagree about what a sensitive query param is. It filters the
  // canonical denylist and drops the fragment, where Keycloak returns the OAuth
  // code.
  const scrubbed = scrubUrl(url);
  try {
    const parsed = new URL(scrubbed);
    // Rebuilding from protocol + host + path drops three things at once: the
    // query string (a param OUTSIDE the denylist can still hold whatever a
    // merchant typed into a search box), the fragment, and any `user:pass@`
    // userinfo. Origin plus route shape is the part worth keeping in a
    // signature, and it is also the part that is stable across occurrences.
    return `${parsed.protocol}//${parsed.host}${routeClassFromPathname(parsed.pathname)}`;
  } catch {
    // scrubUrl answers '[invalid-url]' for anything it cannot parse.
    return '<URL>';
  }
};

// Free-text `key=value` / `key: value` pairs whose key is a known secret name.
// Built from sentry.ts's denylist so a newly minted secret param is added in
// exactly one place.
const SENSITIVE_ASSIGNMENT_RE = new RegExp(
  `\\b(?:${Array.from(SENSITIVE_QUERY_PARAMS).map(escapeRegExp).join('|')})\\b\\s*[=:]\\s*(?:"[^"]*"|'[^']*'|\\S+)`,
  'gi',
);

const OPAQUE_RUN_RE = /(?<![A-Za-z0-9+/=_-])[A-Za-z0-9+/=_-]{16,}(?![A-Za-z0-9+/=_-])/g;

/**
 * True for a long run that reads as an opaque identifier rather than as code.
 * A length threshold alone is not enough: `getDashboardBrandSelection` is 26
 * characters of real signal, while `9f8e7d6c5b4a3210` is a secret. Entropy in
 * the cheap sense — mixed classes, or all hex — separates them.
 */
const looksOpaque = (run: string): boolean => {
  if (/^\d+$/.test(run)) return false; // a long number reads better as <N>
  if (/^[0-9a-f]+$/i.test(run)) return true; // hex id, content hash, sha
  return /\d/.test(run) && /[A-Za-z]/.test(run);
};

const REDACTION_RULES: readonly TextRule[] = [
  { pattern: /\b(?:https?|wss?|ftp):\/\/[^\s"'`<>)\]}]+/gi, replace: normalizeUrlForShape },
  { pattern: /\b(?:data|blob|filesystem):[^\s"'`<>)\]}]+/gi, replace: () => '<URL>' },
  // Credentials survive any generic heuristic because the scheme names them.
  { pattern: /\b(?:bearer|basic)\s+[^\s"'`,;]+/gi, replace: (match) => `${match.split(/\s+/)[0]} <TOKEN>` },
  { pattern: SENSITIVE_ASSIGNMENT_RE, replace: (match) => `${match.split(/\s*[=:]/, 1)[0]}=<TOKEN>` },
  { pattern: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, replace: () => '<EMAIL>' },
  {
    pattern: /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    replace: () => '<UUID>',
  },
  { pattern: /\b\d{1,3}(?:\.\d{1,3}){3}\b/g, replace: () => '<IP>' },
  // Conservative IPv6: at least two colon-separated hex groups, which also
  // covers the `::1` loopback. Deliberately not RFC-complete — a false positive
  // costs one placeholder, a false negative persists an address.
  { pattern: /(?<![\w:.])(?:[0-9a-f]{0,4}:){2,}[0-9a-f]{0,4}(?![\w:.])/gi, replace: () => '<IP>' },
  { pattern: OPAQUE_RUN_RE, replace: (match) => (looksOpaque(match) ? '<TOKEN>' : match) },
];

const SHAPE_RULES: readonly TextRule[] = [
  // Quoted contents are where a merchant-typed value ends up when it is
  // interpolated into a message, and where property names end up in the
  // TypeError family. Both are volatile, so both become one placeholder.
  { pattern: /"[^"]*"|'[^']*'|`[^`]*`/g, replace: () => '<STR>' },
  { pattern: /\d+/g, replace: () => '<N>' },
];

const applyRules = (value: string, rules: readonly TextRule[]): string =>
  rules.reduce((current, rule) => current.replace(rule.pattern, rule.replace), value);

const collapse = (value: string): string =>
  // Control characters are stripped rather than kept so the NUL used to join
  // name and shape into the hash input cannot occur inside either part.
  value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();

/** The stable shape of a message: every volatile or sensitive run replaced. */
export const normalizeMessage = (raw: string): string => {
  const bounded = raw.slice(0, MAX_RAW_MESSAGE_LENGTH);
  const shaped = collapse(applyRules(applyRules(bounded, REDACTION_RULES), SHAPE_RULES));
  return shaped.length > MAX_MESSAGE_SHAPE_LENGTH
    ? `${shaped.slice(0, MAX_MESSAGE_SHAPE_LENGTH)}...`
    : shaped;
};

const sanitizeErrorName = (raw: string): string => {
  // A custom Error subclass can assign anything to `name`, including
  // interpolated input, so it gets the same redaction the message does before
  // being clamped to an identifier-ish shape.
  const redacted = applyRules(raw.slice(0, MAX_RAW_MESSAGE_LENGTH), REDACTION_RULES);
  const identifier = redacted.replace(/[^A-Za-z0-9_$.]/g, '_').slice(0, MAX_ERROR_NAME_LENGTH);
  return identifier.replace(/^_+|_+$/g, '') || 'Error';
};

const FNV_OFFSET_BASIS_32 = 0x811c9dc5;

/** FNV-1a 32-bit as 8 lowercase hex digits. Deterministic and synchronous. */
export const fnv1a32Hex = (input: string): string => {
  let hash = FNV_OFFSET_BASIS_32;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    // hash * 16777619 (the 32-bit FNV prime) expressed as shifts: a plain
    // multiply exceeds the 53-bit float mantissa and silently drops low bits,
    // which would make the hash depend on how far through the string it is.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
};

interface ErrorDescription {
  name: string;
  message: string;
}

const describeError = (value: unknown): ErrorDescription => {
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }
  if (typeof value === 'string') {
    return { name: 'Error', message: value };
  }
  if (isObject(value)) {
    // A rejection can carry any object. Read only `name` and `message`, never
    // serialize the object — that is how a rejected API response body, or a
    // form value someone attached to a custom error, would leak wholesale.
    return {
      name: typeof value.name === 'string' ? value.name : 'NonError',
      message: typeof value.message === 'string' ? value.message : '',
    };
  }
  return { name: 'NonError', message: `non-Error value of type ${typeof value}` };
};

/** The signature and the two fields that produced it, for one thrown value. */
export const fingerprintError = (
  value: unknown,
): { signature: string; name: string; messageShape: string } => {
  const described = describeError(value);
  const name = sanitizeErrorName(described.name);
  const messageShape = normalizeMessage(described.message);
  // The name is part of the hashed input because two different exception types
  // can normalize to the same shape (`<STR> is not a function`) and must not
  // share a signature.
  return { signature: fnv1a32Hex(`${name}\u0000${messageShape}`), name, messageShape };
};

// ──────────────────────────────────────────────────────────────────────
// Buffer
// ──────────────────────────────────────────────────────────────────────

interface PendingError {
  name: string;
  messageShape: string;
  occurrences: number;
  firstSeenMs: number;
  lastSeenMs: number;
}

const pending = new Map<string, PendingError>();
let pendingRouteClass: string | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Identity of every value already counted. React re-throws an error its
// boundary has handled so devtools and `window.onerror` still see it, so one
// render crash can arrive twice — once via <Sentry.ErrorBoundary onError>, once
// via the window listener. Object identity is the right discriminator here: a
// genuinely repeated failure constructs a NEW Error each time, so counting per
// object can never collapse two distinct occurrences into one.
const countedValues = new WeakSet<object>();

const cancelScheduledFlush = (): void => {
  if (flushTimer === null) return;
  clearTimeout(flushTimer);
  flushTimer = null;
};

const scheduleFlush = (): void => {
  if (flushTimer !== null) return;
  // Scheduled lazily off the first buffered error, so nothing ticks at all
  // while the page is healthy.
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushClientErrors();
  }, FLUSH_INTERVAL_MS);
};

const releaseTag = (): string => {
  const release: unknown = import.meta.env.VITE_SENTRY_RELEASE;
  return typeof release === 'string' && release !== '' ? release : UNKNOWN_RELEASE;
};

const buildPayload = (
  drained: ReadonlyArray<readonly [string, PendingError]>,
  routeClass: string,
  sinkFailures: ClientErrorSinkFailure[],
): ClientErrorPayload => ({
  // Key order mirrors the agreed wire contract so a serialized body can be
  // diffed against the spec by eye.
  schema: CLIENT_ERROR_SCHEMA,
  release: releaseTag(),
  routeClass,
  errors: drained.map(([signature, entry]) => ({
    signature,
    name: entry.name,
    messageShape: entry.messageShape,
    occurrences: entry.occurrences,
    firstSeen: new Date(entry.firstSeenMs).toISOString(),
    lastSeen: new Date(entry.lastSeenMs).toISOString(),
  })),
  sinkFailures,
});

const sendPayload = (
  body: string,
  droppedEntries: number,
  reportedFailures: readonly ClientErrorSinkFailure[],
): void => {
  const url = buildApiUrl(CLIENT_ERROR_PATH);
  // `text/plain;charset=UTF-8` — which is what sendBeacon sets for a string
  // body — is a CORS-safelisted content type, so the request is not
  // preflighted. The dashboard and the API are on different origins in every
  // deployed environment, and an `application/json` body would therefore
  // trigger an OPTIONS preflight that a browser routinely never completes once
  // the page is unloading, silently dropping the one flush that matters most.
  // The fetch fallback sets the same header so both paths look identical to the
  // endpoint.
  let beaconAccepted = false;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      beaconAccepted = navigator.sendBeacon(url, body);
    }
  } catch {
    recordSinkFailure('send_beacon_error', 0);
    beaconAccepted = false;
  }
  if (beaconAccepted) {
    acknowledgeSinkFailures(reportedFailures);
    return;
  }

  if (typeof globalThis.fetch !== 'function') {
    recordSinkFailure('fetch_unavailable', droppedEntries);
    return;
  }

  try {
    void globalThis
      .fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        // The endpoint is anonymous. Nothing here should carry a session.
        credentials: 'omit',
      })
      .then((response) => {
        if (response.ok) acknowledgeSinkFailures(reportedFailures);
        else recordSinkFailure('fetch_http_error', droppedEntries);
      })
      .catch(() => {
        // Do not retry during unload: retain bounded accounting for the next
        // real beacon instead of turning an outage into a request storm.
        recordSinkFailure('fetch_rejected', droppedEntries);
      });
  } catch {
    recordSinkFailure('fetch_error', droppedEntries);
  }
};

/**
 * Ship whatever is buffered. Safe to call at any time, including with an empty
 * buffer; invoked on a timer, on `visibilitychange` → hidden, and on `pagehide`.
 */
export const flushClientErrors = (): void => {
  let droppedEntries = 0;
  try {
    cancelScheduledFlush();
    if (pending.size === 0) return;
    const drained = Array.from(pending.entries());
    droppedEntries = drained.length;
    const routeClass = pendingRouteClass ?? currentRouteClass();
    // Drained before anything downstream can fail. There is no retry, so a
    // transport that throws must not leave these errors buffered for the next
    // flush to send a second time — and, just as important, a payload that
    // cannot be built must not wedge the buffer so that every later flush dies
    // on the same entry and the sink goes silent for the rest of the session.
    pending.clear();
    pendingRouteClass = null;
    const reportedFailures = snapshotSinkFailures();
    sendPayload(
      JSON.stringify(buildPayload(drained, routeClass, reportedFailures)),
      droppedEntries,
      reportedFailures,
    );
  } catch {
    recordSinkFailure('payload_error', Math.max(1, droppedEntries));
  }
};

const record = (value: unknown): void => {
  const routeClass = currentRouteClass();
  // One payload describes one route. If the user navigated since the buffer
  // opened, ship what is there before mixing in an error from somewhere else —
  // otherwise the recorded route is wherever they happened to be at flush time,
  // which is the one field a reader cannot sanity-check against anything else.
  if (pending.size > 0 && pendingRouteClass !== routeClass) {
    flushClientErrors();
  }
  pendingRouteClass = routeClass;

  const { signature, name, messageShape } = fingerprintError(value);
  const now = Date.now();
  const existing = pending.get(signature);
  if (existing) {
    // Saturating counter: a signature that reaches the ceiling is a runaway
    // loop, where the exact count has stopped carrying information. lastSeen
    // keeps advancing so the record still shows how long the burst ran.
    if (existing.occurrences < MAX_OCCURRENCES_PER_SIGNATURE) {
      existing.occurrences += 1;
    }
    existing.lastSeenMs = now;
    scheduleFlush();
    return;
  }
  if (pending.size >= MAX_TRACKED_SIGNATURES) {
    // A full buffer drops the new signature rather than growing. A page
    // emitting more than MAX_TRACKED_SIGNATURES distinct failures inside one
    // flush window is already unambiguously broken, and the ones we hold say so.
    // Count every omitted error so the next beacon also says how incomplete
    // that bounded view is instead of making the overflow look like a quiet
    // surface.
    recordSinkFailure('signature_cap', 1);
    return;
  }
  pending.set(signature, {
    name,
    messageShape,
    occurrences: 1,
    firstSeenMs: now,
    lastSeenMs: now,
  });
  scheduleFlush();
};

/**
 * Record one thrown value. The manual entry point, used by the root
 * `<Sentry.ErrorBoundary onError>` in main.tsx — a React render crash is caught
 * by the boundary and never reaches `window.onerror` in a production build.
 */
export const reportClientError = (error: unknown): void => {
  safely(() => {
    if (isObject(error)) {
      if (countedValues.has(error)) return;
      countedValues.add(error);
    }
    record(error);
  });
};

const isResourceLoadError = (event: ErrorEvent): boolean => {
  // A failed <img>/<script>/<link> fires `error` at the element, carrying the
  // element as target and no error object. These are skipped deliberately: the
  // only thing such an event carries is the asset URL, which is precisely what
  // this sink does not persist, and there is no exception to fingerprint. They
  // also do not bubble, so a bubble-phase window listener never sees one in a
  // browser — this guard covers anything that dispatches at window directly.
  const target: unknown = event.target;
  return isObject(target) && typeof target.tagName === 'string';
};

export const handleWindowError = (event: Event): void => {
  safely(() => {
    const errorEvent = event as ErrorEvent;
    if (isResourceLoadError(errorEvent)) return;
    // A cross-origin script failure arrives with no error object and the
    // message "Script error." — still worth a count, so fall back to the
    // message rather than dropping the event.
    reportClientError(errorEvent.error ?? errorEvent.message);
  });
};

export const handleUnhandledRejection = (event: Event): void => {
  safely(() => {
    reportClientError((event as PromiseRejectionEvent).reason);
  });
};

export const handlePageHide = (): void => {
  flushClientErrors();
};

export const handleVisibilityChange = (): void => {
  safely(() => {
    // 'hidden' is the last state a mobile browser reliably delivers before it
    // freezes or discards the tab, so `pagehide` on its own loses flushes there.
    if (document.visibilityState === 'hidden') {
      flushClientErrors();
    }
  });
};

/**
 * Attach the beacon. Called once at module scope in main.tsx, before
 * `initSentry()`, so the independent sink is up even if the Sentry SDK's own
 * initialization throws. Passing module-level handlers means `addEventListener`
 * dedupes an accidental double install.
 */
export const installClientErrorBeacon = (): void => {
  safely(() => {
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });
};

/**
 * Detach every listener and drop whatever is buffered, without sending it.
 * Production installs once at boot and never calls this; it exists so a test
 * can hand the shared jsdom window back clean, the same way
 * chunkReloadGuard.test.ts detaches its own listener.
 */
export const uninstallClientErrorBeacon = (): void => {
  safely(() => {
    window.removeEventListener('error', handleWindowError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    window.removeEventListener('pagehide', handlePageHide);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    cancelScheduledFlush();
    pending.clear();
    pendingRouteClass = null;
    sinkFailureCounts.clear();
  });
};
